import React, { useState } from 'react';
import './UserForm.css';

const UserForm = ({ onClose, onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    dateOfBirth: '',
    gender: '',
    relationStatus: '',
    employmentType: '',
    contactNumber: '',
    email: ''
  });

  const [errors, setErrors] = useState({
    contactNumber: '',
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    // Validate contact number
    if (name === 'contactNumber') {
      const phoneRegex = /^[0-9]{10}$/;
      setErrors(prev => ({
        ...prev,
        contactNumber: phoneRegex.test(value) ? '' : 'Please enter a valid 10-digit phone number'
      }));
    }

    // Validate email
    if (name === 'email') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      setErrors(prev => ({
        ...prev,
        email: emailRegex.test(value) ? '' : 'Please enter a valid email address'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate contact number and email before submission
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
    const newErrors = {
      contactNumber: phoneRegex.test(formData.contactNumber) ? '' : 'Please enter a valid 10-digit phone number',
      email: emailRegex.test(formData.email) ? '' : 'Please enter a valid email address'
    };
    
    setErrors(newErrors);
  
    // Only submit if there are no errors
    if (!newErrors.contactNumber && !newErrors.email) {
      setIsSubmitting(true);
      
      try {
        // Simulate API delay for better UX
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Generate a unique customer ID
        const customerId = 'CUST_' + Date.now();
        
        // Save to localStorage
        localStorage.setItem('customerId', customerId);
        localStorage.setItem('userDetails', JSON.stringify({
          ...formData,
          customerId: customerId,
          savedAt: new Date().toISOString()
        }));
        
        // Also save to a list of all customers (for future reference)
        const existingCustomers = JSON.parse(localStorage.getItem('allCustomers') || '[]');
        existingCustomers.push({
          ...formData,
          customerId: customerId,
          savedAt: new Date().toISOString()
        });
        localStorage.setItem('allCustomers', JSON.stringify(existingCustomers));
        
        alert('Personal details saved successfully!');
        onClose();
        onNavigate('risk-profile');
      } catch (error) {
        console.error('Error saving form data:', error);
        alert('An error occurred while saving your details. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="form-overlay">
      <div className="form-container">
        <button className="close-btn" onClick={onClose} type="button">
          ×
        </button>
        <h2>Personal Details</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="age">Age</label>
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleChange}
              required
              min="18"
              max="100"
            />
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">Date of Birth</label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="relationStatus">Relationship Status</label>
            <select
              id="relationStatus"
              name="relationStatus"
              value={formData.relationStatus}
              onChange={handleChange}
              required
            >
              <option value="">Select Status</option>
              <option value="single">Single</option>
              <option value="married">Married</option>
              <option value="divorced">Divorced</option>
              <option value="widowed">Widowed</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="employmentType">Type of Employment</label>
            <select
              id="employmentType"
              name="employmentType"
              value={formData.employmentType}
              onChange={handleChange}
              required
            >
              <option value="">Select Employment Type</option>
              <option value="fullTime">Full-Time</option>
              <option value="partTime">Part-Time</option>
              <option value="selfEmployed">Self-Employed</option>
              <option value="unemployed">Unemployed</option>
              <option value="retired">Retired</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="contactNumber">Contact Number</label>
            <input
              type="tel"
              id="contactNumber"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              placeholder="Enter 10-digit number"
              required
            />
            {errors.contactNumber && <span className="error-message">{errors.contactNumber}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email ID</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@email.com"
              required
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-buttons">
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Submit'}
            </button>
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;